import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { BUILTIN_LOCALES, BUILTIN_PACKS } from './builtin.js';
import { createTranslator, resolveLocale, type RuntimeCatalogues, type Translate } from './translate.js';
import type { LanguageInfo, Locale } from './types.js';

const STORAGE_KEY = 'platform.locale';

export interface I18nContextValue {
  /** Effective locale. */
  locale: Locale;
  /** Selectable (enabled) languages. */
  languages: readonly LanguageInfo[];
  /** Stores the user's choice (user preference wins over browser and instance default). */
  setLocale: (locale: Locale) => void;
  /** Apply an authenticated account choice without changing this browser's saved preference. */
  applyLocale: (locale: Locale | null) => void;
  t: Translate;
  /** Formats a date/time in the current locale. */
  formatDate: (value: Date | string, options?: Intl.DateTimeFormatOptions) => string;
  /** Formats a number in the current locale. */
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/** The user's stored language choice, or null. Safe without storage. */
export function detectStoredLocale(): string | null {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    return null; // Storage blocked (private mode etc.).
  }
}

function persistLocale(locale: Locale): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, locale);
  } catch {
    // Not persisted; the choice still applies for this session.
  }
}

const BUILTIN_LANGUAGES: readonly LanguageInfo[] = BUILTIN_LOCALES.map((locale) => ({ locale, name: BUILTIN_PACKS[locale]?.name ?? locale }));

export interface I18nProviderProps {
  children: ReactNode;
  /** Forces a locale (tests); otherwise: stored choice → browser → instance default → English. */
  initialLocale?: Locale;
  /** Enabled languages (from the server); defaults to the built-in packs. */
  languages?: readonly LanguageInfo[];
  /** Instance default language (admin setting). */
  defaultLocale?: Locale;
  /** Runtime pack messages per locale (overrides + extra languages), merged over the built-in packs. */
  runtime?: RuntimeCatalogues;
  /** Called with the effective locale, e.g. to load its runtime pack. */
  onLocaleChange?: (locale: Locale) => void;
}

export function I18nProvider({ children, initialLocale, languages = BUILTIN_LANGUAGES, defaultLocale, runtime, onLocaleChange }: I18nProviderProps) {
  const [preferred, setPreferred] = useState<string | null>(() => initialLocale ?? detectStoredLocale());

  const available = useMemo(() => languages.map((language) => language.locale), [languages]);
  const locale = useMemo(
    () => resolveLocale({ preferred, browser: globalThis.navigator?.languages ?? [], available, instanceDefault: defaultLocale }),
    [preferred, available, defaultLocale],
  );

  useEffect(() => {
    document.documentElement.lang = locale;
    onLocaleChange?.(locale);
  }, [locale, onLocaleChange]);

  const setLocale = useCallback((next: Locale) => {
    persistLocale(next);
    setPreferred(next);
  }, []);
  const applyLocale = useCallback((next: Locale | null) => setPreferred(next ?? detectStoredLocale()), []);

  const value = useMemo<I18nContextValue>(() => {
    const dateFormats = new Map<string, Intl.DateTimeFormat>();
    return {
      locale,
      languages,
      setLocale,
      applyLocale,
      t: createTranslator(locale, runtime),
      formatDate: (input, options = { dateStyle: 'medium', timeStyle: 'short' }) => {
        const cacheKey = JSON.stringify(options);
        let format = dateFormats.get(cacheKey);
        if (!format) {
          format = new Intl.DateTimeFormat(locale, options);
          dateFormats.set(cacheKey, format);
        }
        return format.format(typeof input === 'string' ? new Date(input) : input);
      },
      formatNumber: (input, options) => new Intl.NumberFormat(locale, options).format(input),
    };
  }, [locale, languages, setLocale, applyLocale, runtime]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Access translations and locale helpers. Must be used inside `I18nProvider`. */
export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside <I18nProvider>.');
  return context;
}
